import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { ErrorAlert } from "@/components/ui/ErrorAlert";
import { SettingsSection } from "@/components/SettingsSection";
import { SettingsRow } from "@/components/SettingsRow";
import { useAccountQuery, useUpdateEmailMutation } from "@/features/account/hooks/useAccount";
import { extractMessage } from "@/lib/errors";
import type { Account } from "@/features/auth/types";

function EmailForm({ account }: { account: Account }) {
  const { t } = useTranslation();
  const updateEmail = useUpdateEmailMutation();
  const [email, setEmail] = useState(account.email);
  const [error, setError] = useState<string | null>(null);

  const dirty = email.trim().length > 0 && email !== account.email;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await updateEmail.mutateAsync({ email });
    } catch (err) {
      setError(extractMessage(err));
    }
  }

  return (
    <div className="flex flex-col items-end gap-2">
      <form onSubmit={handleSubmit} className="flex items-center gap-2">
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          required
          className="w-64"
        />
        <Button type="submit" size="sm" disabled={!dirty || updateEmail.isPending}>
          {updateEmail.isPending ? t("common.saving") : t("common.save")}
        </Button>
      </form>
      {error && <ErrorAlert message={error} />}
    </div>
  );
}

export function ProfileSection() {
  const { t } = useTranslation();
  const { data: account, isLoading } = useAccountQuery();

  if (isLoading || !account) {
    return (
      <SettingsSection title={t("account.profile.title")}>
        <div className="px-4 py-3 text-sm text-muted-foreground">{t("common.loading")}</div>
      </SettingsSection>
    );
  }

  return (
    <SettingsSection title={t("account.profile.title")}>
      <SettingsRow
        label={t("account.profile.emailLabel")}
        description={t("account.profile.emailDescription")}
      >
        <EmailForm account={account} />
      </SettingsRow>
      <SettingsRow
        label={t("account.profile.planLabel")}
        description={t("account.profile.planDescription")}
      >
        <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-foreground">
          {account.plan === "PAID" ? t("plan.PAID") : t("plan.FREE")}
        </span>
      </SettingsRow>
    </SettingsSection>
  );
}
